import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip,
  Skeleton,
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
} from '@mui/icons-material';
import { userApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

function LeaderboardPage() {
  const { user } = useAuth();
  const { data: leaderboard, isLoading } = useQuery({
    queryKey: ['leaderboard'],
    queryFn: () => userApi.getLeaderboard(20).then((r) => r.data),
  });

  if (isLoading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>Rangliste</Typography>
        <Card>
          <CardContent>
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} height={60} sx={{ mb: 1 }} />
            ))}
          </CardContent>
        </Card>
      </Box>
    );
  }

  const getMedalColor = (rank: number) => {
    switch (rank) {
      case 1: return '#FFD700';
      case 2: return '#C0C0C0';
      case 3: return '#CD7F32';
      default: return undefined;
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Rangliste</Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Die aktivsten Lerner der Plattform
      </Typography>

      <Card>
        <List>
          {leaderboard?.map((entry: any, index: number) => {
            const isCurrentUser = entry.id === user?.id;
            const medalColor = getMedalColor(index + 1);

            return (
              <ListItem
                key={entry.id}
                sx={{
                  bgcolor: isCurrentUser ? 'primary.light' : undefined,
                  '&:not(:last-child)': { borderBottom: '1px solid', borderColor: 'divider' },
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                  }}
                >
                  {medalColor ? (
                    <TrophyIcon sx={{ color: medalColor, fontSize: 28 }} />
                  ) : (
                    <Typography variant="h6" color="text.secondary">
                      {index + 1}
                    </Typography>
                  )}
                </Box>
                <ListItemAvatar>
                  <Avatar
                    src={entry.avatarUrl}
                    sx={{ bgcolor: isCurrentUser ? 'primary.dark' : 'primary.main' }}
                  >
                    {entry.displayName?.charAt(0) || '?'}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography fontWeight={isCurrentUser ? 700 : 400}>
                        {entry.displayName || 'Anonym'}
                      </Typography>
                      {isCurrentUser && (
                        <Chip size="small" label="Du" color="primary" />
                      )}
                    </Box>
                  }
                  secondary={entry.currentSkillLevel?.name || 'Einsteiger'}
                />
                <Typography variant="h6" color="primary">
                  {entry.totalPoints.toLocaleString('de-DE')}
                </Typography>
              </ListItem>
            );
          })}
        </List>
      </Card>
    </Box>
  );
}

export default LeaderboardPage;
